import { useEffect, useState } from "react";
import type { RecordingSourceVideoUrls } from "./ProctoringVideoPlayer";
import { produce } from "immer";

type ProctoredMediaSegmentMetadata =
	| { ok: true; durationMs: number; width: number; height: number }
	| { ok: false; error: Error };
const getProctoredMediaSegmentMetadata = ({ src }: { src: string }): Promise<ProctoredMediaSegmentMetadata> => {
	return new Promise((resolve) => {
		const video = document.createElement("video");
		video.preload = "metadata";
		video.muted = true;
		video.playsInline = true;
		video.style.display = "none";
		video.crossOrigin = "anonymous";

		const timeout = setTimeout(() => {
			const err = new Error("Failed to load media metadata within timeout");
			resolve({
				ok: false,
				error: err,
			});
			handleCleanup();
		}, 60000);

		const handleCleanup = () => {
			try {
				clearTimeout(timeout);
				video.removeEventListener("loadedmetadata", handleLoaded);
				video.removeEventListener("error", handleError);
				video.pause();
				video.setAttribute("src", "");
				video.load();
				video.remove();
			} catch (error) {
				console.error(`Error cleaning up video element: ${JSON.stringify(error)}`);
			}
		};

		function handleLoaded() {
			resolve({
				ok: true,
				durationMs: video.duration * 1000,
				width: video.videoWidth,
				height: video.videoHeight,
			});
			handleCleanup();
		}

		function handleError() {
			const mediaError = video.error;
			const err = new Error(
				mediaError
					? `Failed to load media metadata (code ${mediaError.code}): ${mediaError.message}`
					: "Failed to load media metadata",
			);
			resolve({
				ok: false,
				error: err,
			});
			handleCleanup();
		}

		video.addEventListener("loadedmetadata", handleLoaded);
		video.addEventListener("error", handleError);

		video.src = src;
		document.body.appendChild(video);
	});
};

type ProctoredMediaSegment = {
	url: string;
} & ProctoredMediaSegmentMetadata;
type ProctoredMediaSource = Record<number | string, ProctoredMediaSegment>;
export type ProctoredMedia = Record<number | string, ProctoredMediaSource>;

const mapMediaSourceToProctoredMedia = async (source: RecordingSourceVideoUrls): Promise<ProctoredMedia> => {
	const entries = await Promise.all(
		Object.entries(source).map(async ([mediaSourceKey, segments]) => {
			const segmentEntries = await Promise.all(
				Object.entries(segments).map(async ([segmentKey, segmentUrl]) => {
					const metadata = await getProctoredMediaSegmentMetadata({ src: segmentUrl });
					return [segmentKey, { ...metadata, url: segmentUrl }] as const;
				}),
			);
			return [mediaSourceKey, Object.fromEntries(segmentEntries)] as const;
		}),
	);
	return Object.fromEntries(entries);
};

/**
 * shared structure for media associated with a proctoring session
 *
 * eg:
 * {
 *   webcam: {
 *     "1": {
 *       "1": { ok: true, durationMs: 10000, width: 1920, height: 1080 },
 *       "2": { ok: true, durationMs: 10000, width: 1920, height: 1080 },
 *     },
 *   },
 *   screencapture: {
 *     "1": {
 *       "abc": { ok: true, durationMs: 10000, width: 1920, height: 1080 },
 *       "def": { ok: false },
 *     },
 *   },
 *   roomScan: {},
 * }
 */
export type ProctoredMediaContainer = {
	webcam: ProctoredMedia;
	screencapture: ProctoredMedia;
	roomScan: ProctoredMedia;
};

/**
 * map recording segment urls to an object with additional metadata
 */
export const useProctoredMediaContainer = ({
	webcam,
	screencapture,
	roomScan,
}: {
	webcam: RecordingSourceVideoUrls;
	screencapture: RecordingSourceVideoUrls;
	roomScan: RecordingSourceVideoUrls;
}): ProctoredMediaContainer => {
	const [proctoredMediaContainer, setProctoredMediaContainer] = useState<ProctoredMediaContainer>({
		webcam: {},
		screencapture: {},
		roomScan: {},
	});

	useEffect(() => {
		let cancelled = false;

		const proctoredMediaInputs = [
			{ mediaType: "webcam", source: webcam },
			{ mediaType: "screencapture", source: screencapture },
			{ mediaType: "roomScan", source: roomScan },
		] as const;

		proctoredMediaInputs.forEach(({ mediaType, source }) => {
			void mapMediaSourceToProctoredMedia(source).then((mappedMedia) => {
				if (cancelled) return;
				setProctoredMediaContainer((previousContainer) =>
					produce(previousContainer, (draftContainer) => {
						draftContainer[mediaType] = mappedMedia;
					}),
				);
			});
		});

		return () => {
			cancelled = true;
		};
	}, [webcam, screencapture, roomScan]);

	return proctoredMediaContainer;
};
